import { findLast, flatten, flattenDeep } from 'lodash';
import moment from 'moment';
import URI from 'urijs';
import { formatPertimmDate } from 'Store/utils/searchHelper';
import {
  DEPARTURE_CITY_FACET_ID,
  DEPARTURE_DATE_MAX_FACET_ID,
  DEPARTURE_DATE_MIN_FACET_ID,
  DURATION_FACET,
  LOCALE_FACET_ID,
  OFFER_ID_FACET_ID,
  PRODUCT_FACET_ID,
} from 'Utils/faceting/constants';
const FILTER_PARAM = 'filters';
export type Filter = {
  name: string;
  value: string | number;
  operation?: string;
};
export function composeFiltersAsPayload(...filters: Filter[][]): string {
  const formData: string[] = flattenDeep(filters).reduce((acc, { name, value, operation }) => {
    if (value) {
      acc.push(`${FILTER_PARAM}[${name}][values][]=${URI.encode(value)}`);
    }

    if (value && operation) {
      acc.push(`${FILTER_PARAM}[${name}][operations][]=${URI.encode(operation)}`);
    }

    return acc;
  }, []);
  return formData.join('&');
}

function buildFilters({ name, value, operation }): Filter[] {
  return value
    ? [
        {
          name,
          value,
          operation,
        },
      ]
    : [];
}

export function getLocaleFilters(locale: string, operation: string = 'and'): Filter[] {
  return buildFilters({
    name: LOCALE_FACET_ID,
    value: locale,
    operation,
  });
}
export const getFlexibilityDates = ({
  departureDate,
  flexibility,
}: {
  departureDate?: string;
  flexibility?: number;
}) => {
  if (!flexibility || !departureDate) {
    return {
      minDepartureDate: departureDate,
      maxDepartureDate: departureDate,
    };
  }

  const minDepartureDate = moment(departureDate).subtract(flexibility, 'days');
  return {
    minDepartureDate: minDepartureDate.isAfter(moment().add(1, 'days'))
      ? minDepartureDate.valueOf()
      : moment().add(1, 'days').valueOf(),
    maxDepartureDate: moment(departureDate).add(flexibility, 'days').valueOf(),
  };
};
export function getDepartureDateFilters({
  departureDate,
  flexibility = 0,
  locale,
  operation = 'and',
}: {
  departureDate?: string;
  flexibility?: number;
  locale: string;
  operation?: string;
}): Filter[] {
  const { minDepartureDate, maxDepartureDate } = getFlexibilityDates({
    departureDate,
    flexibility,
  });
  return [
    ...buildFilters({
      name: DEPARTURE_DATE_MIN_FACET_ID,
      value: minDepartureDate && formatPertimmDate(moment(minDepartureDate), locale),
      operation,
    }),
    ...buildFilters({
      name: DEPARTURE_DATE_MAX_FACET_ID,
      value: maxDepartureDate && formatPertimmDate(moment(maxDepartureDate), locale),
      operation,
    }),
  ];
}
export function getDepartureCityFilters({
  facets = [],
  defaultValue = '',
  operation = 'and',
}: {
  facets?: ActiveFacet[];
  defaultValue?: string;
  operation?: string;
}): Filter[] {
  const departureCityFacet = findLast(facets, ({ id }) => id === DEPARTURE_CITY_FACET_ID) || {};
  const value = departureCityFacet.name || defaultValue;
  return buildFilters({
    name: DEPARTURE_CITY_FACET_ID,
    value,
    operation,
  });
}
export function getDurationFilters(duration?: number, operation: string = 'and') {
  return buildFilters({
    name: DURATION_FACET,
    value: duration,
    operation,
  });
}

function getProductIdFilters(id: string, operation: string = 'or'): Filter[] {
  return buildFilters({
    name: PRODUCT_FACET_ID,
    value: id,
    operation,
  });
}

export function getProductIdsFilters(ids: string[] = [], operation?: string): Filter[] {
  return flatten(ids.map((id) => getProductIdFilters(id, operation)));
}
export function getOfferFilters(offerId?: string, operation: string = 'and') {
  return buildFilters({
    name: OFFER_ID_FACET_ID,
    value: offerId,
    operation,
  });
}
const FACETS_TO_OMIT = [
  DEPARTURE_DATE_MIN_FACET_ID,
  DEPARTURE_DATE_MAX_FACET_ID,
  DEPARTURE_CITY_FACET_ID,
  DURATION_FACET,
  OFFER_ID_FACET_ID,
];
export function getFacetsFilters(facets: ActiveFacet[] = [], operation: string = 'and'): Filter[] {
  const filters: Filter[][] = facets
    .filter((facet) => !FACETS_TO_OMIT.includes(facet.id))
    .map((facet) =>
      buildFilters({
        name: facet.id,
        value: facet.name,
        operation,
      }),
    );
  return flatten(filters);
}
